export interface NavItem {
  id: string;
  title: string;
  href: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  isPremium?: boolean;
  isExperimental?: boolean;
  isComingSoon?: boolean;
  isDeprecated?: boolean;
  isExternal?: boolean;
  subitems?: NavItem[];
}

export interface NavSection {
  id: string;
  title: string;
  description?: string;
  items: NavItem[];
}

// ---- Overview page data types ----
export interface OverviewEndpoint {
  href: string;
  title: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  description: string;
  useCases: string[];
  isPremium?: boolean;
  isExperimental?: boolean;
}

export interface EndpointCategory {
  category: string;
  description: string;
  endpoints: OverviewEndpoint[];
}

// Restructured navigation following Next.js docs pattern: Getting Started → Guides → API Reference
export const navSections: NavSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    items: [
      {
        id: 'overview',
        title: 'Overview',
        href: '/overview',
      }
    ]
  },
  {
    id: 'weather-data-layers',
    title: 'Weather Data Layers',
    items: [
      {
        id: 'data-layers',
        title: 'Overview',
        href: '/data-layers',
      },
      {
        id: 'core-data-fields',
        title: 'Core Data Fields',
        href: '/data-layers/core-data-fields',
      },
      {
        id: 'historical-data-fields',
        title: 'Historical Data Fields',
        href: '/historical-data-fields',
      },
      {
        id: 'weather-codes',
        title: 'Weather Codes',
        href: '/data-layers/weather-codes',
      }
    ]
  },
  {
    id: 'api-fundamentals',
    title: 'API Fundamentals',
    items: [
      {
        id: 'quick-start',
        title: 'Quick Start Guide',
        href: '/getting-started/quick-start',
      },
      {
        id: 'access-keys',
        title: 'Access Keys',
        href: '/getting-started/access-keys'
      },
      {
        id: 'rate-limiting',
        title: 'Rate Limiting & Tokens',
        href: '/getting-started/rate-limiting',
        isComingSoon: true
      },
      {
        id: 'webhooks',
        title: 'Webhooks',
        href: '/getting-started/webhooks',
        isComingSoon: true
      },
      {
        id: 'versioning',
        title: 'Versioning',
        href: '/getting-started/versioning',
        isComingSoon: true
      },
      {
        id: 'formats',
        title: 'Formats',
        href: '/getting-started/formats',
        isComingSoon: true
      },
      {
        id: 'pagination',
        title: 'Pagination',
        href: '/getting-started/pagination',
        isComingSoon: true
      },
      {
        id: 'error-handling',
        title: 'Error Handling',
        href: '/error-handling',
      },
      {
        id: 'tools-libraries',
        title: 'Tools & Libraries',
        href: '/getting-started/tools-libraries',
        isComingSoon: true
      },
      {
        id: 'changelog',
        title: 'Changelog',
        href: '/getting-started/changelog',
        isComingSoon: true
      }
    ]
  },
  {
    id: 'weather-forecast-realtime',
    title: 'Weather Forecast & Realtime',
    items: [
      {
        id: 'weather-forecast',
        title: 'Weather Forecast',
        href: '/weather-forecast',
        method: 'GET'
      },
      {
        id: 'realtime-weather',
        title: 'Realtime Weather',
        href: '/realtime-weather',
        method: 'GET'
      },
      {
        id: 'weather-timelines',
        title: 'Weather Timelines',
        href: '/weather-timelines',
        method: 'POST'
      },
      {
        id: 'weather-on-routes',
        title: 'Weather on Routes',
        href: '/weather-on-routes',
        method: 'POST'
      }
    ]
  },
  {
    id: 'weather-maps',
    title: 'Weather Maps',
    items: [
      {
        id: 'weather-maps',
        title: 'Weather Maps',
        href: '/weather-maps',
        method: 'GET'
      }
    ]
  },
  {
    id: 'historical-weather',
    title: 'Historical Weather',
    items: [
      {
        id: 'recent-history',
        title: 'Weather Recent History',
        href: '/recent-history',
        method: 'GET'
      },
      {
        id: 'historical-weather',
        title: 'Historical API',
        href: '/historical-weather',
        method: 'POST'
      },
      {
        id: 'climate-normals',
        title: 'Historical Climate Normals',
        href: '/historical-climate-normals',
        method: 'GET'
      }
    ]
  },
  {
    id: 'tomorrow-io-api',
    title: 'Monitoring API',
    items: [
      {
        id: 'locations-api',
        title: 'Locations API',
        href: '/locations-api',
        method: 'GET'
      },
      {
        id: 'insights-api',
        title: 'Insights API',
        href: '/insights-api',
        method: 'GET'
      },
      {
        id: 'alerts-api',
        title: 'Alerts API',
        href: '/alerts-api',
        method: 'GET'
      },
      {
        id: 'events-api',
        title: 'Events API',
        href: '/api/events',
        method: 'GET',
        isComingSoon: true
      },
      {
        id: 'custom-events-api',
        title: 'Custom Events API',
        href: '/custom-events-api',
        method: 'POST'
      },
      {
        id: 'on-demand-events-api',
        title: 'On-Demand Events API',
        href: '/on-demand-events-api',
        method: 'POST'
      },
      {
        id: 'severe-weather-events',
        title: 'Severe Weather Events',
        href: '/severe-weather-events',
        subitems: [
          {
            id: 'air-events',
            title: 'Air',
            href: '/severe-weather-events/air'
          },
          {
            id: 'fires-events',
            title: 'Fires',
            href: '/severe-weather-events/fires'
          },
          {
            id: 'wind-events',
            title: 'Wind',
            href: '/severe-weather-events/wind'
          },
          {
            id: 'winter-events',
            title: 'Winter',
            href: '/severe-weather-events/winter'
          },
          {
            id: 'thunderstorms-events',
            title: 'Thunderstorms',
            href: '/severe-weather-events/thunderstorms'
          },
          {
            id: 'floods-events',
            title: 'Floods',
            href: '/severe-weather-events/floods'
          },
          {
            id: 'temperature-events',
            title: 'Temperature',
            href: '/severe-weather-events/temperature'
          },
          {
            id: 'tropical-events',
            title: 'Tropical',
            href: '/severe-weather-events/tropical'
          },
          {
            id: 'marine-events',
            title: 'Marine',
            href: '/severe-weather-events/marine'
          },
          {
            id: 'fog-events',
            title: 'Fog',
            href: '/severe-weather-events/fog'
          },
          {
            id: 'tornado-events',
            title: 'Tornado',
            href: '/severe-weather-events/tornado'
          }
        ]
      },
      {
        id: 'notifications-api',
        title: 'Notifications API',
        href: '/api/notifications',
        method: 'POST',
        isComingSoon: true
      }
    ]
  },
  {
    id: 'platform-data-integration',
    title: 'Platform Data Integration',
    items: [
      {
        id: 'flight-schedule',
        title: 'Integrating your Flight Schedule',
        href: '/integration/flight-schedule',
        isComingSoon: true
      },
      {
        id: 'stations-data',
        title: 'Integrating your Stations Data',
        href: '/integration/stations-data',
        isComingSoon: true
      },
      {
        id: 'scheduled-events',
        title: 'Integrating your scheduled events',
        href: '/integration/scheduled-events',
        isComingSoon: true
      }
    ]
  }
];

// Overview-page endpoint list, sourced from home page. Keep synchronized!
export const endpointCategories: EndpointCategory[] = [
  {
    category: 'Real-Time Weather',
    description: 'Get current weather conditions and immediate forecasts',
    endpoints: [
      {
        href: '/realtime-weather',
        title: 'Realtime Weather API',
        method: 'GET',
        description: 'Get current weather conditions for any location with up-to-the-minute accuracy. Returns temperature, humidity, wind speed, precipitation, and more.',
        useCases: ['Current conditions', 'Dashboard widgets', 'Mobile apps']
      }
    ]
  },
  {
    category: 'Weather Forecasting',
    description: 'Access detailed weather predictions and forecast data',
    endpoints: [
      {
        href: '/weather-forecast',
        title: 'Weather Forecast API',
        method: 'GET',
        description: 'Access detailed weather forecasts up to 15 days ahead with customizable time steps (1 hour to daily intervals) and comprehensive weather parameters.',
        useCases: ['Planning applications', 'Agriculture', 'Outdoor events']
      },
      {
        href: '/weather-timelines',
        title: 'Weather Timelines API',
        method: 'POST',
        description: 'Create highly customized weather data timelines with specific parameters, locations, and time intervals. Most flexible endpoint for complex queries.',
        useCases: ['Custom dashboards', 'Multi-location analysis', 'Research projects']
      }
    ]
  },
  {
    category: 'Historical Weather',
    description: 'Access past weather data for analysis and research',
    endpoints: [
      {
        href: '/recent-history',
        title: 'Recent History API',
        method: 'GET',
        description: 'Retrieve weather data from the past 6 hours to 30 days. Perfect for recent weather analysis and verification.',
        useCases: ['Performance analysis', 'Data verification', 'Recent trends']
      },
      {
        href: '/historical-weather',
        title: 'Historical Weather API',
        method: 'POST',
        description: 'Query historical weather conditions from 2000 onwards with customizable location, fields, and time ranges. Supports statistical aggregations.',
        useCases: ['Climate research', 'Trend analysis', 'Backtesting models']
      },
      {
        href: '/historical-climate-normals',
        title: 'Historical Climate Normals API',
        method: 'POST',
        description: 'Access climate normals data providing statistical weather averages over a 20-year period (2000-2020) with daily aggregation and comprehensive statistical measures.',
        useCases: ['Climate analysis', 'Agricultural planning', 'Baseline comparisons']
      }
    ]
  },
  {
    category: 'Data Layers & References',
    description: 'Comprehensive references for weather data fields and codes',
    endpoints: [
      {
        href: '/data-layers/weather-codes',
        title: 'Weather Codes Reference',
        method: 'GET',
        description: 'Complete reference for Tomorrow.io weather codes including basic codes, full-day codes, day/night variants, and weather icons library for visual representation.',
        useCases: ['Weather icon selection', 'Condition mapping', 'Visual weather displays']
      },
      {
        href: '/data-layers/core-data-fields',
        title: 'Core Data Fields',
        method: 'GET',
        description: 'Comprehensive documentation of all data fields available through the Tomorrow.io Weather API including descriptions, units, availability, and use cases.',
        useCases: ['API parameter selection', 'Data field reference', 'Integration planning']
      },
      {
        href: '/historical-data-fields',
        title: 'Historical Data Fields',
        method: 'GET',
        description: 'Complete reference for historical weather data fields available from 2000 onwards, including field descriptions, units, data types, availability periods, and interface compatibility.',
        useCases: ['Historical analysis', 'Climate research', 'Trend analysis', 'Backtesting models']
      }
    ]
  },
  {
    category: 'Specialized Services',
    description: 'Advanced weather services for specific use cases',
    endpoints: [
      {
        href: '/locations-api',
        title: 'Locations API',
        method: 'GET',
        description: 'Manage predefined locations for weather monitoring and alerts. Create, retrieve, update, and delete locations with support for point, polygon, and polyline geometries.',
        useCases: ['Location management', 'Weather monitoring', 'Alert systems']
      },
      {
        href: '/insights-api',
        title: 'Insights API',
        method: 'GET',
        description: 'Create and manage weather-based insights for automated monitoring and alerting. Define custom conditions using weather parameters, set severity levels, and organize insights with tags.',
        useCases: ['Weather alerts', 'Condition monitoring', 'Automated notifications']
      },
      {
        href: '/alerts-api',
        title: 'Alerts API',
        method: 'GET',
        description: 'Create and manage weather alerts for automated monitoring and notifications. Configure alerts based on insights or lightning conditions, set notification schedules, and organize alerts with tags.',
        useCases: ['Weather notifications', 'Alert management', 'Lightning detection', 'Critical weather monitoring']
      },
      {
        href: '/custom-events-api',
        title: 'Custom Events API',
        method: 'POST',
        description: 'Create and manage custom vector events feed to quickly aggregate weather events derived from your custom insights. Configure weather thresholds, data points, and time frames with GeoJSON and JSON format support.',
        useCases: ['Custom event aggregation', 'Weather thresholds', 'Vector data integration']
      },
      {
        href: '/on-demand-events-api',
        title: 'On-Demand Events API',
        method: 'POST',
        description: 'Generate real-time weather insights and forecasts for specific locations using custom weather conditions or existing insights. Create event timelines with activation states and detailed weather metadata.',
        useCases: ['Real-time insights', 'Weather monitoring', 'Event timeline analysis']
      },
      {
        href: '/weather-maps',
        title: 'Weather Maps API',
        method: 'GET',
        description: 'Access weather map tiles and overlays for precipitation, temperature, wind, and other meteorological data visualization.',
        useCases: ['Interactive maps', 'Weather visualization', 'GIS applications']
      },
      {
        href: '/weather-on-routes',
        title: 'Weather on Routes API',
        method: 'POST',
        description: 'Get weather conditions along a specific route or path with detailed waypoint analysis for transportation and logistics.',
        useCases: ['Fleet management', 'Trip planning', 'Logistics optimization']
      },
      {
        href: '/severe-weather-events',
        title: 'Severe Weather Events',
        method: 'GET',
        description: 'Access standardized severe weather events from multiple sources for comprehensive situational awareness. Monitor local, regional, and national alerts across various weather event categories.',
        useCases: ['Emergency management', 'Risk assessment', 'Situational awareness']
      }
    ]
  }
] ; 